import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { serverSubscriptionsAPI } from '../services/api';

const SubscriptionPlansContext = createContext();

export const useSubscriptionPlans = () => {
  const context = useContext(SubscriptionPlansContext);
  if (!context) {
    throw new Error('useSubscriptionPlans must be used within SubscriptionPlansProvider');
  }
  return context;
};

export const SubscriptionPlansProvider = ({ children }) => {
  const [plans, setPlans] = useState([]);
  const [plansMap, setPlansMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { data, isLoading: queryLoading, error } = useQuery(
    'subscriptionPlans',
    () => serverSubscriptionsAPI.getServerPlans().then(res => {
      // API returns {success: true, data: {plans: [...]}}
      // res.data is the axios response.data which is {success: true, data: {...}}
      // So we need to access res.data.data.plans
      return res.data?.data?.plans || res.data?.plans || [];
    }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (fetchedPlans) => {
        // fetchedPlans is already the plans array from the API call above
        setPlans(fetchedPlans || []);
        
        // Create a map for quick lookup by ID
        const map = {};
        (fetchedPlans || []).forEach(plan => {
          map[plan.id] = plan;
        });
        setPlansMap(map);
      }
    }
  );

  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  // Sync data when query data changes
  useEffect(() => {
    if (data) {
      setPlans(data || []);
      
      // Create a map for quick lookup by ID
      const map = {};
      (data || []).forEach(plan => {
        map[plan.id] = plan;
      });
      setPlansMap(map);
    }
  }, [data]);

  // Helper functions
  const getPlanById = (planId) => {
    return plansMap[planId] || null;
  };

  const getPlanByTier = (tier) => {
    return plans.find(plan => plan.id === tier) || null;
  };

  const hasFeature = (planId, feature) => {
    const plan = getPlanById(planId);
    return plan?.features?.[feature] || false;
  };

  const getLimit = (planId, limitName) => {
    const plan = getPlanById(planId);
    return plan?.limits?.[limitName] || null;
  };

  const value = {
    plans,
    plansMap,
    isLoading,
    error,
    getPlanById,
    getPlanByTier,
    hasFeature,
    getLimit,
  };

  return (
    <SubscriptionPlansContext.Provider value={value}>
      {children}
    </SubscriptionPlansContext.Provider>
  );
};

